import { getJson, ApiError } from '@/lib/api/client';
import { apiRevalidateSeconds } from '@/lib/env';
import {
  catalogProviderListSchema,
  type CatalogProviderList
} from './catalog.types';

const DEFAULT_LIMIT = 20;
// Allow up to 1000 to support map display showing all providers (matches legacy pageSize)
const MAX_LIMIT = 1000;

export const CATALOG_PROVIDER_ORDERS = [
  'staggered',
  'distance',
  'recent'
] as const;
export type CatalogProviderOrder = (typeof CATALOG_PROVIDER_ORDERS)[number];

export type CatalogSearchFilters = {
  page?: number;
  limit?: number;
  search?: string;
  breedSlug?: string;
  province?: string;
  city?: string;
  availableNow?: boolean;
  includeListings?: boolean;
  includeWaitlists?: boolean;
  order?: CatalogProviderOrder;
};

export type NormalizedCatalogSearchFilters = {
  page: number;
  limit: number;
  search?: string;
  breedSlug?: string;
  province?: string;
  city?: string;
  availableNow?: boolean;
  includeListings?: boolean;
  includeWaitlists?: boolean;
  order?: CatalogProviderOrder;
};

export class CatalogApiError extends Error {
  status?: number;
  constructor(message: string, options?: { status?: number; cause?: unknown }) {
    super(message);
    this.name = 'CatalogApiError';
    this.status = options?.status;
    if (options?.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

const trim = (value?: string | null) => {
  if (typeof value !== 'string') return undefined;
  const next = value.trim();
  return next.length > 0 ? next : undefined;
};

const isOrderOption = (value: unknown): value is CatalogProviderOrder => {
  return (
    typeof value === 'string' &&
    CATALOG_PROVIDER_ORDERS.includes(value as CatalogProviderOrder)
  );
};

export const normalizeCatalogSearchFilters = (
  filters: CatalogSearchFilters = {}
): NormalizedCatalogSearchFilters => {
  const page = Number.isFinite(filters.page)
    ? Math.max(1, Math.floor(filters.page as number))
    : 1;
  const limit = Number.isFinite(filters.limit)
    ? Math.min(Math.max(1, Math.floor(filters.limit as number)), MAX_LIMIT)
    : DEFAULT_LIMIT;
  const search = trim(filters.search);
  const breedSlug = trim(filters.breedSlug);
  const province = trim(filters.province);
  const city = trim(filters.city);
  const availableNow =
    typeof filters.availableNow === 'boolean'
      ? filters.availableNow
      : undefined;
  const includeListings =
    typeof filters.includeListings === 'boolean'
      ? filters.includeListings
      : undefined;
  const includeWaitlists =
    typeof filters.includeWaitlists === 'boolean'
      ? filters.includeWaitlists
      : undefined;
  const order = isOrderOption(filters.order) ? filters.order : undefined;
  return {
    page,
    limit,
    search,
    breedSlug,
    province,
    city,
    availableNow,
    includeListings,
    includeWaitlists,
    order
  } satisfies NormalizedCatalogSearchFilters;
};

export const serializeCatalogSearchFilters = (
  filters?: CatalogSearchFilters | NormalizedCatalogSearchFilters
): URLSearchParams => {
  const normalized = normalizeCatalogSearchFilters(filters);
  const params = new URLSearchParams();
  params.set('page', normalized.page.toString());
  params.set('limit', normalized.limit.toString());
  if (normalized.search) params.set('search', normalized.search);
  if (normalized.breedSlug) params.set('breedSlug', normalized.breedSlug);
  if (normalized.province) params.set('province', normalized.province);
  if (typeof normalized.availableNow === 'boolean') {
    params.set('availableNow', String(normalized.availableNow));
  }
  if (typeof normalized.includeListings === 'boolean') {
    params.set('includeListings', String(normalized.includeListings));
  }
  if (typeof normalized.includeWaitlists === 'boolean') {
    params.set('includeWaitlists', String(normalized.includeWaitlists));
  }
  if (normalized.order) params.set('order', normalized.order);
  return params;
};

export const buildCatalogRequestUrl = (
  filters?: CatalogSearchFilters | NormalizedCatalogSearchFilters
) => {
  const params = serializeCatalogSearchFilters(filters);
  const query = params.toString();
  const base = '/catalog/providers';
  return query.length > 0 ? `${base}?${query}` : base;
};

export type FetchCatalogProvidersOptions = {
  /** Skip Next.js data cache (for client-side filter changes) */
  skipCache?: boolean;
};

export const fetchCatalogProviders = async (
  filters?: CatalogSearchFilters | NormalizedCatalogSearchFilters,
  options?: FetchCatalogProvidersOptions
): Promise<CatalogProviderList> => {
  const url = '/catalog/providers';
  const params = serializeCatalogSearchFilters(filters);
  try {
    const payload = await getJson<
      typeof catalogProviderListSchema,
      CatalogProviderList
    >(url, {
      query: Object.fromEntries(params.entries()),
      schema: catalogProviderListSchema,
      // Skip cache for client-side filter changes to ensure fresh data
      cache: options?.skipCache ? 'no-store' : undefined,
      next: options?.skipCache
        ? undefined
        : { revalidate: apiRevalidateSeconds, tags: ['catalog:providers'] }
    });
    return payload;
  } catch (error) {
    if (error instanceof ApiError) {
      throw new CatalogApiError(
        `Catalog API request failed (${error.status ?? 'unknown'})`,
        { status: error.status, cause: error.body ?? error.cause }
      );
    }
    throw error;
  }
};
